import { useQuery } from '@tanstack/react-query';
import { meLocationsService } from '../services/meLocations';
import { useAuth } from './useAuth';

export type { MeLocation } from '../services/meLocations';

export const meLocationsKeys = {
  all: ['meRestaurants'] as const,
  list: (uid: string | null) => [...meLocationsKeys.all, 'list', uid] as const,
};

/**
 * Fetch the restaurants the signed-in user can access (via
 * `GET /api/v1/users/me/locations`). Gated on an authenticated user so it
 * never fires before the auth listener has resolved a Firebase session.
 *
 * Each row carries `employeeGuid` (nullable) — the caller's own employee GUID
 * at that restaurant — which staff surfaces use to detect linked-employee
 * access.
 */
export const useMeLocations = () => {
  const { data: auth } = useAuth();
  const uid = auth?.firebaseUser?.uid ?? null;

  return useQuery({
    queryKey: meLocationsKeys.list(uid),
    queryFn: () => meLocationsService.getMyRestaurants(),
    enabled: !!uid,
    // This query gates the whole management app, so its failure budget has to
    // be bounded rather than inherited. The global default (3 retries, 1s/2s/4s
    // backoff) sat on top of the shared client's 120s socket timeout — up to
    // four 2-minute attempts, ~8 minutes before anything resolved, while
    // ManagementAccessGate had already given up at 15s. A refresh was the only
    // way out. One bounded retry plus the 20s per-request timeout in the
    // service caps the whole thing at ~41s and lets a transient stall recover
    // on its own.
    retry: 1,
    retryDelay: 500,
  });
};
